import {
  Form,
  Input,
  InputNumber,
  Button,
  Typography,
  Row,
  Tooltip,
  Switch,
} from "antd"
import React, { useState } from "react"
import { IFormSchemaOutput } from "@utils/form.utils"
import { FormListFieldData } from "antd/lib/form/FormList"
import {
  HeaderDiv,
  MainDiv,
  NestedDivContent,
  NestedHeaderDiv,
} from "@components/forms/dags/styles"

const { Title, Paragraph } = Typography
const FormItem = Form.Item

const normaliseBooleanValue = (value: any) => {
  if (typeof value !== "string") return value

  const normalised = value.trim().toLowerCase()
  if (normalised === "true") return true
  if (normalised === "false") return false

  return value
}

const normaliseRecordBySchema = (
  record: any,
  schema: IFormSchemaOutput[],
) => {
  if (!record) return record

  const next = { ...record }
  schema.forEach((item) => {
    if (item.inputType === "boolean" && item.key in next) {
      next[item.key] = normaliseBooleanValue(next[item.key])
    }

    if (item.inputType === "_nested_") {
      const nesting = Object.values(item).find(
        (value) => Array.isArray(value) && value,
      ) as IFormSchemaOutput[] | undefined

      if (nesting && Array.isArray(next[item.key])) {
        next[item.key] = next[item.key].map((value: any) =>
          normaliseRecordBySchema(value, nesting),
        )
      }
    }
  })
  return next
}

const normaliseFormDataBySchema = (
  formData: any,
  schema: IFormSchemaOutput[],
) => ({
  ...formData,
  data: Array.isArray(formData?.data)
    ? formData.data.map((record: any) => normaliseRecordBySchema(record, schema))
    : formData?.data,
})

const Description = ({
  label,
  description,
}: {
  label: string
  description: string
}) => {
  return (
    <Tooltip
      title={() => <span style={{ fontSize: 13 }}>{description}</span>}
      color={"green"}
    >
      <Paragraph
        ellipsis={{ rows: 1, expandable: false, symbol: "more" }}
        style={{ padding: 0, margin: 0 }}
      >
        {label}
      </Paragraph>
    </Tooltip>
  )
}

const FormInput: React.FunctionComponent<{
  item: any
  field: any
  defaultValue: string
  isRequired: boolean
  initialValues: boolean
}> = ({ item, field, defaultValue, isRequired, initialValues }) => {
  const initialValue =
    item.inputType === "boolean"
      ? normaliseBooleanValue(defaultValue)
      : defaultValue

  const renderInputs = () => {
    const inputType = item.inputType
    switch (inputType) {
      case "boolean":
        return <Switch />
      case "string":
        // Schema opts a field into a multi-line box via "format": "textarea"
        // (e.g. dataset/asset `description`). Antd's plain Input strips
        // pasted newlines (a single-line <input> can't hold them), which
        // silently collapsed paragraph breaks before they ever reached the
        // submitted JSON; TextArea preserves them.
        return item.format === "textarea" ? (
          <Input.TextArea autoSize={{ minRows: 3 }} />
        ) : (
          <Input />
        )
      case "number":
        return <InputNumber />
      default:
        return <Input />
    }
  }
  return (
    <FormItem
      key={field.key}
      initialValue={initialValues ? undefined : initialValue}
      label={<Description label={item.name} description={item.description} />}
      name={[field.name, item.key]}
      valuePropName={item.inputType === "boolean" ? "checked" : "value"}
      rules={[{ required: isRequired, message: `${item.name} is required` }]}
    >
      {renderInputs()}
    </FormItem>
  )
}

const DagsForm = ({
  initialValues,
  schema__,
  onFinish,
}: {
  initialValues: any
  schema__: IFormSchemaOutput[]
  onFinish: any
}) => {
  const [showColor, setShowColor] = useState(true)

  const [form] = Form.useForm()

  const handleFinish = (formData: any) => {
    onFinish(normaliseFormDataBySchema(formData, schema__))
  }

  /**
   * Convert data attribute to Array.
   * When data is single element it is not passed as array from backend, so we
   * need to explicitly convert to avoid breaking the form.
   */
  const modInitialValue = () => {
    if (initialValues) {
      const { data } = initialValues
      if (Array.isArray(data)) {
        return normaliseFormDataBySchema(initialValues, schema__)
      } else {
        return normaliseFormDataBySchema({
          ...initialValues,
          data: [data],
        }, schema__)
      }
    }
  }

  const renderForm = (
    field_: FormListFieldData,
    schema_: IFormSchemaOutput[],
    _index: number,
  ) => {
    // const alpha = 10 * (1 * (_index+1))
    const rgba = `rgb(0, 146, 214, ${(_index + 1) * 0.07})`

    const render = (data: IFormSchemaOutput) => {
      const { description, inputType, name, default_, isRequired } = data
      if (inputType !== "_nested_") {
        return (
          <FormInput
            item={data}
            initialValues={!!initialValues}
            field={field_}
            isRequired={isRequired}
            defaultValue={default_}
          />
        )
      } else {
        const nesting = Object.values(data).find(
          (item) => Array.isArray(item) && item,
        )

        return (
          <Form.List
            name={[field_.key, name]}
            initialValue={!initialValues ? [{}] : undefined}
          >
            {(fields, { add, remove, move }) => (
              <Row
                style={{
                  padding: 10,
                  marginBottom: 10,
                  width: "100%",
                  border: "1px solid green",
                  backgroundColor: showColor ? rgba : "white",
                }}
              >
                <NestedHeaderDiv>
                  <p style={{ margin: 0 }}>{description}</p>
                  <Button
                    onClick={() => add()}
                    style={{ marginRight: 10 }}
                    type={"primary"}
                    size={"small"}
                  >
                    Add
                  </Button>
                </NestedHeaderDiv>
                {fields.map((field) => (
                  <NestedDivContent direction={"vertical"} key={field.key}>
                    <Row gutter={10} style={{ padding: "0 5px" }}>
                      {renderForm(field, nesting, _index + 1)}
                    </Row>

                    <Button
                      onClick={() => remove(field.key)}
                      type={"primary"}
                      size={"small"}
                      danger
                    >
                      Delete
                    </Button>
                  </NestedDivContent>
                ))}
              </Row>
            )}
          </Form.List>
        )
      }
    }

    // @ts-ignore
    return schema_.map((item, index) => (
      <div key={index} style={{ width: "100%" }}>
        {render(item)}
      </div>
    ))
  }

  return (
    <Form
      form={form}
      name={"dags__form"}
      onFinish={handleFinish}
      layout={"horizontal"}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      initialValues={modInitialValue()}
    >
      <Row style={{ flexDirection: "row-reverse", flex: 1 }}>
        <Switch
          onChange={(val) => setShowColor(!val)}
          defaultChecked={showColor}
        />
        <p style={{ marginRight: 10 }}>Background:</p>
      </Row>

      <Form.List name={"data"} initialValue={!initialValues ? [{}] : undefined}>
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <MainDiv direction={"vertical"} key={field.key}>
                <HeaderDiv>
                  <Title level={5}>{field.key + 1}. Sheet Information</Title>
                  <div>
                    <Button
                      onClick={() => add()}
                      style={{ marginRight: 10 }}
                      type={"primary"}
                      size={"small"}
                    >
                      Add
                    </Button>
                    <Button
                      onClick={() => remove(field.key)}
                      type={"primary"}
                      size={"small"}
                      danger
                    >
                      Delete
                    </Button>
                  </div>
                </HeaderDiv>
                <Row gutter={10}>{renderForm(field, schema__, 0)}</Row>
              </MainDiv>
            ))}
          </>
        )}
      </Form.List>
      <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
        Submit
      </Button>
    </Form>
  )
}

export default DagsForm
